/* Updated Report Dashboard Toggle Logic */
import { useState, useMemo, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement } from "chart.js"
import SelectDays from "./selectDays";
import { useFetchReportsSummaryQuery } from "../../api";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  Box,
  Popover,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface WarningMessage {
  id: string;
  message: string;
  timestamp: Date;
  type: 'deletion' | 'warning';
  itemName: string;
  gitlabUsername?: string;
  deletedData?: {
    soldQuantity: number;
    givenQuantity: number;
    totalStock: number;
    soldMoney: number;
    stockRemaining: number;
  };
}

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement);

const MenuReport = () => {
  const [days, setDays] = useState<number | 'custom' | 'all'>('all');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [warnings, setWarnings] = useState<WarningMessage[]>([]);
  const [isWarningsExpanded, setIsWarningsExpanded] = useState(false);
  const [isReportTableExpanded, setIsReportTableExpanded] = useState(false);
  const [isLifetimeTableExpanded, setIsLifetimeTableExpanded] = useState(false);

  // Filter states
  const [filterCategory, setFilterCategory] = useState<'all' | 'Snacks' | 'Drinks' | 'Other'>('all');

  useEffect(() => {
    const storedWarnings = localStorage.getItem('menuWarnings');
    if (storedWarnings) {
      const parsedWarnings = JSON.parse(storedWarnings);
      const validWarnings = parsedWarnings.filter((warning: WarningMessage) => {
        const warningTime = new Date(warning.timestamp);
        const now = new Date();
        const hoursDiff = (now.getTime() - warningTime.getTime()) / (1000 * 60 * 60);
        return hoursDiff < 24;
      });
      setWarnings(validWarnings);
      localStorage.setItem('menuWarnings', JSON.stringify(validWarnings));
    }
  }, []);

  const formatTime = (timestamp: Date) => new Date(timestamp).toLocaleString();

  const getTimeRemaining = (timestamp: Date) => {
    const warningTime = new Date(timestamp);
    const now = new Date();
    const hoursDiff = 24 - ((now.getTime() - warningTime.getTime()) / (1000 * 60 * 60));
    if (hoursDiff <= 0) return 'Expired';
    const hours = Math.floor(hoursDiff);
    const minutes = Math.floor((hoursDiff - hours) * 60);
    return hours > 0 ? `${hours}h ${minutes}m remaining` : `${minutes}m remaining`;
  };

  const [customRange, setCustomRange] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: dayjs().subtract(7, 'day'),
    to: dayjs(),
  });

  const [tempRange, setTempRange] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: dayjs().subtract(7, 'day'),
    to: dayjs(),
  });

  const handleApplyCustomRange = () => {
    setCustomRange(tempRange);
    setAnchorEl(null);
  };

  const { from, to } = useMemo(() => {
    if (days === 'all') {
      return { from: undefined, to: undefined };
    }
    let start, end;
    if (days === 'custom') {
      start = customRange.from ? customRange.from.startOf('day') : dayjs().startOf('day');
      end = customRange.to ? customRange.to.endOf('day') : dayjs().endOf('day');
    } else {
      const numDays = Number(days) || 7;
      end = dayjs().endOf('day');
      start = dayjs().subtract(numDays - 1, 'day').startOf('day');
    }
    return {
      from: start.isValid() ? start.toISOString() : dayjs().subtract(7, 'day').toISOString(),
      to: end.isValid() ? end.toISOString() : dayjs().toISOString()
    };
  }, [days, customRange]);

  const isFutureRange = useMemo(() => {
    if (days === 'all') return false;
    const now = dayjs();
    const startDate = dayjs(from);
    const endDate = dayjs(to);
    return startDate.isAfter(now) && endDate.isAfter(now);
  }, [from, to, days]);

  const { data: summary, isLoading: isSummaryLoading } = useFetchReportsSummaryQuery(days === 'all' ? {} : { from, to });

  const aggregates = useMemo(() => {
    const baseline = {
      soldQuantity: 0,
      givenQuantity: 0,
      remainingQuantity: 0,
      revenue: 0,
      totalProducts: 0,
      totalInitialStock: 0
    };

    const results = {
      Snacks: { ...baseline },
      Drinks: { ...baseline },
      Other: { ...baseline }
    };

    if (!summary?.categorySummary) return results;

    summary.categorySummary.forEach((c: any) => {
      const type = (c.categoryType === 'Snacks' || c.categoryType === 'Drinks') ? c.categoryType : 'Other';
      results[type as keyof typeof results].soldQuantity += (c.soldQuantity || 0);
      results[type as keyof typeof results].givenQuantity += (c.givenQuantity || 0);
      results[type as keyof typeof results].remainingQuantity += (c.remainingQuantity || 0);
      results[type as keyof typeof results].revenue += (c.revenue || 0);
      results[type as keyof typeof results].totalProducts += (c.totalProducts || 0);
      results[type as keyof typeof results].totalInitialStock += (c.totalInitialStock || 0);
    });

    return results;
  }, [summary]);

  if (isSummaryLoading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-gray-500 font-medium italic">Loading Report Data...</div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header & Filters Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-4">
          <Typography variant="h5" className="font-bold text-slate-800 tracking-tight">
            Menu Reports
          </Typography>
          <div className="h-6 w-[2px] bg-slate-100 hidden sm:block"></div>
          <FormControl size="small" className="min-w-[160px]">
            <Select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as any)}
              className="bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              sx={{
                '& .MuiSelect-select': { py: 1.2, fontSize: '0.875rem', fontWeight: 600, color: '#334155' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' }
              }}
            >
              <MenuItem value="all" className="text-sm font-medium text-slate-700">All Categories</MenuItem>
              <MenuItem value="Snacks" className="text-sm font-medium text-slate-700">Snacks</MenuItem>
              <MenuItem value="Drinks" className="text-sm font-medium text-slate-700">Drinks</MenuItem>
              <MenuItem value="Other" className="text-sm font-medium text-slate-700">Other</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SelectDays value={days} onChange={(val) => setDays(val)} />
          {days === 'custom' && (
            <button
              onClick={(e) => {
                setTempRange(customRange);
                setAnchorEl(e.currentTarget);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-all shadow-sm"
            >
              <CalendarMonthIcon sx={{ fontSize: 18 }} className="text-slate-500" />
              {customRange.from?.format('MMM DD')} - {customRange.to?.format('MMM DD')}
            </button>
          )}
          {days === 'all' && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-slate-100 text-sm font-semibold text-slate-700 shadow-sm">
              <span>All Time Data</span>
            </div>
          )}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{ className: "rounded-xl shadow-lg border border-slate-100" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography className="font-bold text-slate-800 text-sm">Custom Date Range</Typography>
                <DatePicker label="From" value={tempRange.from} onChange={(val) => setTempRange(prev => ({ ...prev, from: val }))} />
                <DatePicker label="To" value={tempRange.to} onChange={(val) => setTempRange(prev => ({ ...prev, to: val }))} />
                <button
                  onClick={handleApplyCustomRange}
                  className="w-full bg-slate-900 text-white py-2.5 rounded-lg font-semibold hover:bg-slate-800 transition-colors shadow-sm"
                >
                  Apply Range
                </button>
              </Box>
            </LocalizationProvider>
          </Popover>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-white rounded-2xl border border-rose-200 overflow-hidden shadow-sm">
          <div
            onClick={() => setIsWarningsExpanded(!isWarningsExpanded)}
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-rose-50/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-rose-100 p-1.5 rounded-lg">
                <WarningAmberIcon className="text-rose-600" fontSize="small" />
              </div>
              <Typography className="font-bold text-slate-800 text-sm">Active Warnings ({warnings.length})</Typography>
            </div>
            <IconButton size="small" className="text-slate-400 hover:text-slate-600">
              <svg
                width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className={`transition-transform duration-300 ${isWarningsExpanded ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </IconButton>
          </div>
          {isWarningsExpanded && (
            <div className="p-4 border-t border-rose-100 flex flex-wrap gap-3 max-h-[250px] overflow-y-auto bg-slate-50/50">
              {warnings.map((warning) => (
                <div key={warning.id} className="p-3 bg-white rounded-xl border border-rose-200 text-xs min-w-[240px] shadow-sm flex-1">
                  <span className="font-bold text-slate-800 block mb-1.5 text-[13px]">{warning.itemName}</span>
                  <span className="text-slate-600 block mb-3 leading-relaxed">{warning.message}</span>
                  <div className="mt-auto pt-2 border-t border-slate-100 flex justify-between items-center text-[11px] text-slate-500 font-medium">
                    <span>{formatTime(warning.timestamp)}</span>
                    <span className="text-rose-500 font-semibold bg-rose-50 px-2 py-0.5 rounded-md">{getTimeRemaining(warning.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isFutureRange && (
        <div className="bg-sky-50 border border-sky-200 p-4 rounded-xl text-sm text-sky-800 flex items-center gap-3 shadow-sm font-medium">
          <CalendarMonthIcon fontSize="small" className="text-sky-600" />
          <span>Future selection: Showing projected data.</span>
        </div>
      )}

      {/* Main Report Table (Unified Spreadsheet Style) */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        {/* Report Toggle Bar */}
        <div
          onClick={() => setIsReportTableExpanded(!isReportTableExpanded)}
          className="bg-black text-white flex items-center justify-center gap-3 py-3 font-bold uppercase tracking-widest text-xs cursor-pointer hover:bg-slate-900 transition-colors"
        >
          <span>Report - {days === 'all' ? 'All Time' : 'Selected Range'}</span>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            className={`transition-transform duration-300 ${isReportTableExpanded ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        {isReportTableExpanded && (
          <div className="animate-in fade-in slide-in-from-top-1 duration-200 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-4 px-6 text-left w-[120px]"></th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Sold</th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Given</th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Remaining</th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Revenue</th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Variance</th>
                  <th className="py-4 px-4 text-center text-sm font-bold text-slate-800">Stock</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {/* Snacks Row */}
                {(filterCategory === 'all' || filterCategory === 'Snacks') && (
                  <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6 font-bold text-slate-800 text-left">Snacks</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Snacks.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-amber-600 font-medium">{aggregates.Snacks.givenQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Snacks.remainingQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Snacks.revenue?.toLocaleString() || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Snacks.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-slate-800">
                      {aggregates.Snacks.totalInitialStock || 0}
                    </td>
                  </tr>
                )}

                {/* Drinks Row */}
                {(filterCategory === 'all' || filterCategory === 'Drinks') && (
                  <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6 font-bold text-slate-800 text-left">Drinks</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Drinks.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-amber-600 font-medium">{aggregates.Drinks.givenQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Drinks.remainingQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Drinks.revenue?.toLocaleString() || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Drinks.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-slate-800">
                      {aggregates.Drinks.totalInitialStock || 0}
                    </td>
                  </tr>
                )}

                {/* Others Row */}
                {(filterCategory === 'all' || filterCategory === 'Other') && (
                  <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6 font-bold text-slate-800 text-left">Others</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Other.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-amber-600 font-medium">{aggregates.Other.givenQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Other.remainingQuantity || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Other.revenue?.toLocaleString() || 0}</td>
                    <td className="py-4 px-4 text-center text-slate-700">{aggregates.Other.soldQuantity || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-slate-800">
                      {aggregates.Other.totalInitialStock || 0}
                    </td>
                  </tr>
                )}

                {/* Total Row */}
                <tr className="bg-slate-100/80 font-bold border-t border-slate-200">
                  <td className="py-5 px-6 text-slate-900 text-left">Total</td>
                  <td className="py-5 px-4 text-center text-slate-900">{summary?.totalSoldQuantity || 0}</td>
                  <td className="py-5 px-4 text-center text-amber-700">{summary?.totalGivenQuantity || 0}</td>
                  <td className="py-5 px-4 text-center text-slate-900">{summary?.totalRemainingStock || 0}</td>
                  <td className="py-5 px-4 text-center text-slate-900">{summary?.totalRevenue?.toLocaleString() || 0}</td>
                  <td className="py-5 px-4 text-center text-slate-900">{summary?.totalSoldQuantity || 0}</td>
                  <td className="py-5 px-4 text-center text-slate-900">
                    {(summary?.totalStock || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Selected Range Summary Section */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        {/* Toggle Bar */}
        <div
          onClick={() => setIsLifetimeTableExpanded(!isLifetimeTableExpanded)}
          className="bg-black text-white flex items-center justify-center gap-3 py-3 font-bold uppercase tracking-widest text-xs cursor-pointer hover:bg-slate-900 transition-colors"
        >
          <span>{days === 'all' ? 'All Time Summary' : 'Selected Range Summary'}</span>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            className={`transition-transform duration-300 ${isLifetimeTableExpanded ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        {isLifetimeTableExpanded && (
          <div className="animate-in fade-in slide-in-from-top-1 duration-200 p-4 bg-slate-50/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Revenue</p>
                <p className="text-lg font-bold text-emerald-600">RWF {summary?.totalRevenue?.toLocaleString()}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Items Sold</p>
                <p className="text-lg font-bold text-slate-800">{summary?.totalSoldQuantity?.toLocaleString()}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Giveaways (Given)</p>
                <p className="text-lg font-bold text-amber-600">{summary?.totalGivenQuantity?.toLocaleString()}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Current Stock Balance</p>
                <p className="text-lg font-bold text-slate-800">{summary?.totalRemainingStock?.toLocaleString()}</p>
              </div>

            </div>
          </div>
        )}
      </div>


      <div className="flex justify-end">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-[11px] font-medium text-slate-400">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
          Report generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default MenuReport;

export const addMenuWarning = (itemName: string, deletedData?: WarningMessage['deletedData']) => {
  const getGitlabUsername = () => process.env.REACT_APP_GITLAB_USERNAME || 'Unknown User';
  const newWarning: WarningMessage = {
    id: Date.now().toString(),
    message: `All transaction data for "${itemName}" has been cleared by admin.`,
    timestamp: new Date(),
    type: 'deletion',
    itemName,
    gitlabUsername: getGitlabUsername(),
    deletedData
  };
  const existingWarnings = JSON.parse(localStorage.getItem('menuWarnings') || '[]');
  localStorage.setItem('menuWarnings', JSON.stringify([...existingWarnings, newWarning]));
};
